import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  makeStyles,
} from "@material-ui/core"
import { MemberIdentity } from "./MemberIdentity"
import { RoleSelector } from "./RoleSelector"
import { HourReportingRolesTranslations } from "../hooks/use-translations"
import { HourReportingRoleRow, RoleAssignmentKind, RoleSelectorOption } from "../types"

type HourReportingRolesTableProps = {
  orgId: string
  rows: HourReportingRoleRow[]
  translations: HourReportingRolesTranslations
  canUpdateAssignments: boolean
  supportsChecking: boolean
  isAssignmentPending: (memberId: string, kind: RoleAssignmentKind) => boolean
  onOpenRoleDetails: (memberId: string) => void
  onUpdateAssignment: (memberId: string, kind: RoleAssignmentKind, selectedMembers: RoleSelectorOption[]) => void
}

export const HourReportingRolesTable = ({
  orgId,
  rows,
  translations,
  canUpdateAssignments,
  supportsChecking,
  isAssignmentPending,
  onOpenRoleDetails,
  onUpdateAssignment,
}: HourReportingRolesTableProps) => {
  const classes = useStyles()

  const showCheckingColumn = supportsChecking && canUpdateAssignments
  const showApprovingColumn = canUpdateAssignments

  return (
    <div className={classes.tableRoot} data-org-id={orgId}>
      <TableContainer component={Paper} elevation={0}>
        <Table aria-label={translations.tableAriaLabel}>
          <TableHead>
            <TableRow>
              <TableCell>{translations.reporterHeaderText}</TableCell>
              {showCheckingColumn && <TableCell>{translations.checkersHeaderText}</TableCell>}
              {showApprovingColumn && <TableCell>{translations.approversHeaderText}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.member.id}>
                <TableCell className={classes.memberCell}>
                  <MemberIdentity
                    member={row.member}
                    checkerCount={row.reporterDetails.checkedReporters.length}
                    approverCount={row.reporterDetails.approvedReporters.length}
                    translations={translations}
                    supportsChecking={supportsChecking}
                    onOpenRoleDetails={() => onOpenRoleDetails(row.member.id)}
                  />
                </TableCell>
                {showCheckingColumn && (
                  <TableCell className={classes.selectorCell}>
                    <RoleSelector
                      label={translations.checkersInputLabel}
                      roleType="checker"
                      options={row.roleOptions}
                      selectedMembers={row.selectedCheckers}
                      disabled={!canUpdateAssignments || isAssignmentPending(row.member.id, "checker")}
                      onChange={(selectedMembers) => onUpdateAssignment(row.member.id, "checker", selectedMembers)}
                      translations={translations}
                    />
                  </TableCell>
                )}
                {showApprovingColumn && (
                  <TableCell className={classes.selectorCell}>
                    <RoleSelector
                      label={translations.approversInputLabel}
                      roleType="approver"
                      options={row.roleOptions}
                      selectedMembers={row.selectedApprovers}
                      disabled={!canUpdateAssignments || isAssignmentPending(row.member.id, "approver")}
                      onChange={(selectedMembers) => onUpdateAssignment(row.member.id, "approver", selectedMembers)}
                      translations={translations}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  tableRoot: {
    maxWidth: 1440,
    borderRadius: 7,
    width: "100%",
    background: theme.palette.common.white,
    "& .MuiTableContainer-root": {
      borderRadius: 0,
    },
    "& .MuiTableRow-root th": {
      textTransform: "uppercase",
      fontSize: 12,
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  memberCell: {
    width: "28%",
    minWidth: 220,
    verticalAlign: "top",
  },
  selectorCell: {
    width: "36%",
    minWidth: 280,
    verticalAlign: "top",
  },
}))
